/**
 * AdminLogin — Süperadmin giriş sayfası.
 *
 * Merchant Login'in sade kardeşi. OTP yok (admin login password tabanlı);
 * single-field flow, ortalanmış form, dot-bg arka plan. `data-admin-scope`
 * sayesinde primary buton otomatik siyah.
 *
 * Zaten giriş yapmış admin → ADMIN_DASHBOARD_PATH'e redirect edilir.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { useAdminAuth } from "@/lib/admin-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/brand/Logo";
import { ADMIN_DASHBOARD_PATH, ADMIN_CHANGE_PW_PATH } from "@/lib/admin-path";

function readError(err) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
    return err?.message || "Giriş yapılamadı";
}

export default function AdminLogin() {
    const { admin, login } = useAdminAuth();
    const nav = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Already logged in → bounce (change-password zorunluysa oraya)
    useEffect(() => {
        if (admin && admin.email) {
            if (admin.password_must_change) {
                nav(ADMIN_CHANGE_PW_PATH, { replace: true });
                return;
            }
            const from = location.state?.from || ADMIN_DASHBOARD_PATH;
            nav(from, { replace: true });
        }
    }, [admin, nav, location.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (busy || !email || !password) return;
        setBusy(true);
        setError("");
        try {
            const data = await login(email.trim().toLowerCase(), password);
            if (data?.admin?.password_must_change) {
                nav(ADMIN_CHANGE_PW_PATH, { replace: true });
            } else {
                nav(location.state?.from || ADMIN_DASHBOARD_PATH, { replace: true });
            }
        } catch (err) {
            setError(readError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-admin-scope=""
            className="min-h-screen relative bg-background text-foreground overflow-hidden"
            data-testid="admin-login-page"
        >
            <div className="pointer-events-none absolute inset-0 dot-bg opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
            <div className="pointer-events-none absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full blur-3xl opacity-30 bg-foreground/20" />

            <header className="relative z-10 h-16 px-6 md:px-8 flex items-center justify-between">
                <Logo />
                <span
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold tracking-wide uppercase bg-foreground text-background"
                    data-testid="admin-login-badge"
                >
                    <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                    Süper Admin
                </span>
            </header>

            <main
                className="relative z-10 grid place-items-center px-6"
                style={{ minHeight: "calc(100vh - 64px - 48px)" }}
            >
                <div className="w-full max-w-[420px]">
                    <div className="mb-8 flex items-center justify-center">
                        <div className="h-12 w-12 rounded-xl border border-border bg-surface flex items-center justify-center">
                            <Lock className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
                        <div className="text-center space-y-2">
                            <h1 className="font-display text-3xl font-bold tracking-tight">
                                Yönetici Girişi
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                Yetkili kimlik bilgilerinle devam et.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label
                                className="text-xs font-semibold tracking-wide uppercase text-muted-foreground"
                                htmlFor="admin-email"
                            >
                                E-posta
                            </label>
                            <Input
                                id="admin-email"
                                type="email"
                                autoFocus
                                autoComplete="username"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="E-posta adresin"
                                className="h-11 text-sm bg-surface"
                                data-testid="admin-login-email"
                                disabled={busy}
                            />
                        </div>

                        <div className="space-y-2">
                            <label
                                className="text-xs font-semibold tracking-wide uppercase text-muted-foreground"
                                htmlFor="admin-password"
                            >
                                Parola
                            </label>
                            <div className="relative">
                                <Input
                                    id="admin-password"
                                    type={showPw ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••••••"
                                    className="h-11 text-sm bg-surface pr-10"
                                    data-testid="admin-login-password"
                                    disabled={busy}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                                    aria-label={showPw ? "Parolayı gizle" : "Parolayı göster"}
                                    data-testid="admin-login-toggle-pw"
                                    tabIndex={-1}
                                >
                                    {showPw
                                        ? <EyeOff className="h-4 w-4" strokeWidth={1.75} />
                                        : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div
                                className="text-xs text-destructive text-center -mt-2"
                                data-testid="admin-login-error"
                            >
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={busy || !email || !password}
                            className="w-full h-11 bg-foreground text-background hover:bg-foreground/90 font-medium disabled:opacity-60"
                            data-testid="admin-login-submit"
                        >
                            {busy ? "Doğrulanıyor…" : "Giriş Yap"}
                            {!busy && <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={2} />}
                        </Button>

                        <p className="text-xs text-center text-muted-foreground leading-relaxed">
                            Bu alan yalnızca yetkili Sayz yöneticileri içindir.
                        </p>
                    </form>
                </div>
            </main>

            <footer className="relative z-10 h-12 px-6 md:px-8 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>© Sayz {new Date().getFullYear()}</span>
                <span className="font-mono">süper admin</span>
            </footer>
        </div>
    );
}
